import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, fmtINR, fmtINRFull, formatApiError } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, X, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { Label } from "@/components/ui/label";

export default function Approvals() {
  const [queue, setQueue] = useState({ budgets: [], indents: [] });
  const [msg, setMsg] = useState("");
  const load = () => api.get("/approvals/queue").then((r) => setQueue(r.data || { budgets: [], indents: [] }));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">Approval Queue</h1>
        <p className="mt-1.5 text-slate-500">Budgets and indents awaiting your decision. Tap a card to review and act.</p>
      </div>
      {msg && <div data-testid="approvals-msg" className="text-sm text-slate-600">{msg}</div>}
      <Tabs defaultValue="indents">
        <TabsList data-testid="approvals-tabs">
          <TabsTrigger value="indents" data-testid="tab-indents">Indents · {queue.indents.length}</TabsTrigger>
          <TabsTrigger value="budgets" data-testid="tab-budgets">Budgets · {queue.budgets.length}</TabsTrigger>
        </TabsList>
        <TabsContent value="indents" className="pt-4">
          {queue.indents.length === 0 ? (
            <div data-testid="empty-indent-queue" className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">No indents pending your approval.</div>
          ) : (
            <div className="space-y-3">
              {queue.indents.map((i) => <IndentApprovalCard key={i.id} it={i} onDone={(m) => { setMsg(m); load(); }} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="budgets" className="pt-4">
          {queue.budgets.length === 0 ? (
            <div data-testid="empty-budget-queue" className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">No budgets pending your approval.</div>
          ) : (
            <div className="space-y-3">
              {queue.budgets.map((b) => <BudgetApprovalCard key={b.id} it={b} onDone={(m) => { setMsg(m); load(); }} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function IndentApprovalCard({ it, onDone }) {
  const [open, setOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/indents/${it.id}/approve`, { remark: "" }); onDone("Indent approved."); }
    catch (e) { onDone(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try { await api.post(`/indents/${it.id}/reject`, { remark }); onDone("Indent rejected."); setRejectOpen(false); setRemark(""); }
    catch (e) { onDone(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card data-testid={`approval-indent-${it.id}`} className="p-4 border-slate-200">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{it.iow_code}</span>
            <span className="text-sm font-semibold text-slate-800">{it.description}</span>
            {it.urgency === "Urgent" && <StatusChip status="Urgent" />}
            {it.budget_flag && (
              <span data-testid={`approval-flag-${it.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                <AlertTriangle className="h-3 w-3" /> {it.cum_pct_after}% of budget
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1.5">
            {it.project_name} · Qty {it.qty} {it.uom} · raised by {it.raised_by_name} · {fmtDate(it.raised_at)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="text-lg font-bold text-[#0D3B66]">{fmtINR(it.value)}</div>
        </div>
      </div>
      <button
        data-testid={`toggle-details-${it.id}`}
        onClick={() => setOpen((v) => !v)}
        className="mt-3 text-xs text-slate-500 hover:text-[#0D3B66] inline-flex items-center gap-1"
      >
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {open ? "Hide details" : "Show details"}
      </button>
      {open && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <Detail label="Rate" value={fmtINRFull(it.rate)} />
          <Detail label="Budget Qty" value={String(it.budget_qty)} />
          <Detail label="Cumulative Qty" value={String(it.cum_qty_after)} />
          <Detail label="Cumulative %" value={`${it.cum_pct_after}%`} />
          {it.remarks && <div className="col-span-full"><Detail label="Remarks" value={it.remarks} /></div>}
          {it.rate_override_remark && <div className="col-span-full"><Detail label="Rate override" value={it.rate_override_remark} /></div>}
        </div>
      )}
      <div className="mt-4 flex items-center gap-2">
        <Button data-testid={`approve-indent-${it.id}`} onClick={approve} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4 mr-1.5" /> Approve</Button>
        <Button data-testid={`reject-indent-${it.id}`} onClick={() => setRejectOpen(true)} disabled={busy} variant="outline" className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
      </div>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid={`reject-indent-dialog-${it.id}`}>
          <DialogHeader><DialogTitle>Reject indent</DialogTitle></DialogHeader>
          <div className="space-y-2 pt-2">
            <Label>Remark (required)</Label>
            <Textarea data-testid={`reject-indent-remark-${it.id}`} rows={4} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Reason for rejection…" />
          </div>
          <DialogFooter>
            <Button data-testid={`reject-indent-confirm-${it.id}`} onClick={reject} disabled={!remark.trim() || busy} className="bg-rose-600 hover:bg-rose-700">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function BudgetApprovalCard({ it, onDone }) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/projects/${it.project_id}/boq/approve`, { remark: "" }); onDone("Budget approved."); }
    catch (e) { onDone(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try { await api.post(`/projects/${it.project_id}/boq/reject`, { remark }); onDone("Budget rejected."); setRejectOpen(false); setRemark(""); }
    catch (e) { onDone(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card data-testid={`approval-budget-${it.id}`} className="p-4 border-slate-200">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800">{it.project_name} · <span className="text-slate-500">Budget v{it.version}</span></div>
          <div className="text-xs text-slate-500 mt-1">{it.lines?.length || 0} lines · submitted {fmtDate(it.submitted_at)}</div>
        </div>
        <div className="text-lg font-bold text-[#0D3B66]">{fmtINRFull(it.total_amount)}</div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Link to={`/projects/${it.project_id}/boq`}>
          <Button variant="outline" className="rounded-full">View BOQ</Button>
        </Link>
        <Button data-testid={`approve-budget-${it.id}`} onClick={approve} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4 mr-1.5" /> Approve</Button>
        <Button data-testid={`reject-budget-${it.id}`} onClick={() => setRejectOpen(true)} disabled={busy} variant="outline" className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
      </div>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid={`reject-budget-dialog-${it.id}`}>
          <DialogHeader><DialogTitle>Reject budget</DialogTitle></DialogHeader>
          <div className="space-y-2 pt-2">
            <Label>Remark (required)</Label>
            <Textarea data-testid={`reject-budget-remark-${it.id}`} rows={4} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Reason for rejection…" />
          </div>
          <DialogFooter>
            <Button data-testid={`reject-budget-confirm-${it.id}`} onClick={reject} disabled={!remark.trim() || busy} className="bg-rose-600 hover:bg-rose-700">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
